import localforage from "localforage";
import envConfig from "env-config";
import Smartlook from "smartlook-client";

import {
  userStore,
  fileStore,
  sectionStore,
  initialiseFileStore,
  initialiseUserStore,
  initialiseUsersClient,
  initialiseAccessClient,
  initialiseProfilesClient,
  initialiseSearchClient,
  initialiseApolloClient,
  initialiseInviteClient,
  initialiseSectionStore,
  initialiseLoginTokenClient,
  initialiseOrganisationClient,
  initHandleErrors,
  AxiosError,
  authenticationClient,
  initialiseAuthenticationClient,
  initialiseNationalResourcesClient,
  initialiseNotificationsClient,
  initialiseSpacesClient,
  initialiseContentClient,
} from "@eolas-medical/core";

import { Sentry } from "Contexts";
import { AppEnv } from "Utilities/types";
import { initMixpanel } from "API/Analytics";
import { errorStore, apiErrorsToIgnore } from "Stores/ErrorStore";

import { API_ENV_PATH_PREFIXS } from "./general";
import { EolasBabel } from "./babel";
import { Auth, Hub, Amplify } from "aws-amplify";

import { onLogout, logout, onSuccessfulLogin } from "Pages/Authentication/auth.actions";
import { AuthClientParams } from "@eolas-medical/core/src/clients/authentication/authentication.types";
import { initialiseLoginTokenService } from "API/LoginTokenService";
import { apolloClientDefaultOptions, apolloConfig, apolloLinks } from "config";
import { initialiseWebStore } from "Stores/WebStore";
import { initialisePortfolioService } from "modules/portfolio/client/PortfolioService";
import { initialiseMyFilesService } from "modules/myFiles/client/myFiles.client";
import { initialisePortfolioWizardService } from "modules/portfolio/client/PortfolioWizardService";

const authServiceBaseUrl = `https://${envConfig.REACT_APP_API_BASE_URL}/auth/v1`;

export const startup = () => {
  initHandleErrors(apiErrorsToIgnore);
  const eolasBabel = new EolasBabel();

  const awsConfig = {
    Auth: {
      identityPoolId: envConfig.REACT_APP_AWS_COGNITO_IDENTITY_POOL_ID,
      userPoolId: envConfig.REACT_APP_AWS_COGNITO_USER_POOL_ID,
      userPoolWebClientId: envConfig.REACT_APP_WEB_CLIENT_ID,
      endpoint: `${authServiceBaseUrl}/proxy`,
      region: envConfig.REACT_APP_AWS_REGION,
      authenticationFlowType: "USER_PASSWORD_AUTH",
    },
    oauth: {
      domain: envConfig.REACT_APP_COGNITO_APP_DOMAIN,
      redirectSignIn: window.location.origin,
      redirectSignOut: window.location.origin,
      responseType: "code",
    },
    storage: eolasBabel,
  };

  const authConfig: AuthClientParams = {
    amplifyClasses: { Auth, Hub },
    storage: eolasBabel,
    federatedDomains: envConfig.REACT_APP_SSO_DOMAINS.split(", "),
    baseUrl: authServiceBaseUrl,
    onSignIn: onSuccessfulLogin,
    onSignOut: onLogout,
    onRetrieveTokenError: (error: any) => {
      Sentry.addBreadcrumb({
        category: "auth-client",
        message: `"unable to retrieve a valid token": ${error}`,
      });
      errorStore.captureError(error);
    },
  };

  const handleRetryAttempt = async (error: AxiosError, currentRetryAttempt: number) => {
    errorStore.captureError({ error, source: "network" });
    Sentry.addBreadcrumb({
      category: "REST retry",
      message: `Attempting to retry REST request, error: ${error}`,
    });

    if ([401, 403].includes(error.response?.status ?? 0)) {
      if (currentRetryAttempt === 1 && userStore.userSession.isLoggedIn) {
        try {
          await authenticationClient.getToken({ forceRefresh: true });
        } catch (error: any) {
          errorStore.captureError({ error, source: "network" });
          Sentry.addBreadcrumb({
            category: "REST retry",
            message: `Attempt to reauthenticate cognito session with REST retry: ${error}`,
          });
          logout();
        }
        return;
      }
    }
  };

  const initialiseClientsConfig = {
    environment: API_ENV_PATH_PREFIXS[envConfig.REACT_APP_APP_ENV],
    apiBaseUrl: `https://${envConfig.REACT_APP_API_BASE_URL}/`,
    handleRetryAttempt,
  };

  initialiseSectionStore(
    {
      stringify: false,
      debugMode: false,
      name: "sectionStore",
      storage: localforage,
      expireIn: 24 * 60 * 60 * 1000,
    },
    false,
  );

  initialiseFileStore(
    {
      stringify: false,
      debugMode: false,
      name: "fileStore",
      storage: localforage,
      expireIn: 24 * 60 * 60 * 1000,
    },
    false,
  );

  initialiseUserStore(
    {
      stringify: false,
      debugMode: false,
      name: "userStore",
      storage: localforage,
      expireIn: 24 * 60 * 60 * 1000,
    },
    false,
  );

  initialiseWebStore();

  Amplify.configure(awsConfig);
  initialiseAuthenticationClient(authConfig);

  initialiseApolloClient({
    config: apolloConfig,
    customClientLinks: apolloLinks,
    defaultOptions: apolloClientDefaultOptions,
  });

  initialiseInviteClient(initialiseClientsConfig);

  initialiseUsersClient({ ...initialiseClientsConfig });

  initialiseProfilesClient(initialiseClientsConfig);

  initialiseAccessClient(initialiseClientsConfig);

  initialiseNationalResourcesClient(initialiseClientsConfig);

  initialiseOrganisationClient(initialiseClientsConfig);

  initialiseLoginTokenService(initialiseClientsConfig);

  initialisePortfolioService(initialiseClientsConfig);

  initialiseMyFilesService(initialiseClientsConfig);

  initialiseNotificationsClient(initialiseClientsConfig);

  initialisePortfolioWizardService(initialiseClientsConfig);

  initialiseSpacesClient(initialiseClientsConfig);

  initialiseContentClient(initialiseClientsConfig);

  initialiseSearchClient({
    environment: API_ENV_PATH_PREFIXS[envConfig.REACT_APP_APP_ENV],
    apiBaseUrl:
      envConfig.REACT_APP_APP_ENV === AppEnv.PRODUCTION
        ? "https://search.eolas.click"
        : `https://${API_ENV_PATH_PREFIXS[envConfig.REACT_APP_APP_ENV]}.search.eolas.click`,
    handleRetryAttempt,
  });

  initialiseLoginTokenClient({
    environment: API_ENV_PATH_PREFIXS[envConfig.REACT_APP_APP_ENV],
    apiBaseUrl: `https://${envConfig.REACT_APP_API_BASE_URL}/`,
  });
  initMixpanel();
};

const initiateSessionSentry = () => {
  Sentry.configureScope((scope) => {
    scope.addEventProcessor(async (event) => {
      event.contexts = {
        smartLook: {
          session_id: Smartlook.sessionId,
          timestamp: new Date().getTime(),
        },
      };
      return event;
    });
  });
};

export const smartLookStartRecording = () => {
  initiateSessionSentry();
  Smartlook.init(envConfig.REACT_APP_SMARTLOOK_KEY);
};

export const globalWaitForHydration = async () => {
  const hydrationResults = await Promise.allSettled([
    sectionStore.isHydrated,
    userStore.isHydrated,
    fileStore.isHydrated,
    authenticationClient.isHydrated,
  ]);

  hydrationResults.forEach((outcome) => {
    if (outcome.status === "rejected") {
      errorStore.captureError(outcome.reason);
    }
  });
};
